import { CalendarMinus2, Lock } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";

const LockedContent = () => (
  <div className="flex flex-col items-center justify-center py-12 gap-2">
    <Lock className="size-7 text-gray-300" />
    <p className="text-sm text-gray-400 font-popinsMedium">Permission required</p>
  </div>
);

const RecentActivity = () => {
  const loginSuccess = useSelector((state) => state.auth?.loginSuccess);
  const isAssistant = loginSuccess?.data?.roleName === "assistant";
  const ap = loginSuccess?.data?.assistantPermissions;
  const can = (p) => !isAssistant || ap?.[p] === true;

  const [recentEvents, setRecentEvents] = useState([]);
  const [recentCollaborations, setRecentCollaborations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRecentActivities();
  }, []);

  const fetchRecentActivities = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.GET_RECENT_ACTIVITIES}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();

      if (response.ok && result.statusCode === 200) {
        const data = result.data;
        setRecentEvents(data.recentEvents || []);
        setRecentCollaborations(data.recentCollaborations || []);
      } else {
        console.error("Failed to fetch recent activities:", result.message);
      }
    } catch (error) {
      console.error("Error fetching recent activities:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? "minute" : "minutes"} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
    if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
    return date.toLocaleDateString();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusText = (collaboration) => {
    if (collaboration.type === "received") {
      if (collaboration.status === "PENDING") {
        return "Action Required";
      } else if (collaboration.status === "ACCEPTED") {
        return "Accepted";
      } else if (collaboration.status === "REJECTED") {
        return "Declined";
      }
    } else {
      // Sent requests
      if (collaboration.status === "PENDING") {
        return "Waiting for response";
      } else if (collaboration.status === "ACCEPTED") {
        return "Accepted by NGO";
      } else if (collaboration.status === "REJECTED") {
        return "Declined by NGO";
      }
    }
    return collaboration.status;
  };

  const getRequestTitle = (collaboration) => {
    if (collaboration.type === "received") {
      return `Request from ${collaboration.organizationName}`;
    } else {
      return `Request to ${collaboration.organizationName}`;
    }
  };

  const activitiesPanel = (
    <div className="bg-white shadow rounded-lg px-5 py-6 w-full border border-border">
      <div className="flex justify-between items-center">
        <h2 className="font-popinsSemiBold text-xl">Recent Activities</h2>
        {can("events") && (
          <p className="font-popinsSemiBold text-sm cursor-pointer text-themeColor hover:underline" onClick={() => navigate("/dashboard/manage-events")}>
            View All
          </p>
        )}
      </div>
      {!can("events") ? <LockedContent /> : isLoading ? (
        <div className="flex justify-center items-center py-10">
          <p className="text-gray-400 font-popinsRegular">Loading activities...</p>
        </div>
      ) : recentEvents.length === 0 ? (
        <div className="flex justify-center items-center py-10">
          <p className="text-gray-400 font-popinsRegular">No recent events</p>
        </div>
      ) : (
        recentEvents.map((event, index) => (
          <div key={index} className="flex gap-x-2 items-center mt-5">
            <img src="/assets/images/icons/notification.svg" alt="" className="size-[50px]" />
            <div>
              <p className="font-popinsMedium text-base">{event.title} Posted</p>
              <p className="font-popinsRegular text-black/50 text-sm pt-1">{formatTimeAgo(event.createdAt)}</p>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const collaborationsPanel = (
    <div className="bg-white shadow rounded-lg px-6 py-6 w-full border border-border">
      <div className="flex justify-between">
        <h2 className="font-popinsSemiBold text-base lg:text-xl">Recent Collaboration Requests</h2>
        {can("collaboration") && (
          <p className="font-popinsSemiBold text-sm lg:text-base cursor-pointer text-themeColor hover:underline" onClick={() => navigate("/dashboard/collaboration-workspace")}>
            View All
          </p>
        )}
      </div>
      {!can("collaboration") ? <LockedContent /> : isLoading ? (
        <div className="flex justify-center items-center py-10">
          <p className="text-gray-400 font-popinsRegular">Loading collaborations...</p>
        </div>
      ) : recentCollaborations.length === 0 ? (
        <div className="flex justify-center items-center py-10">
          <p className="text-gray-400 font-popinsRegular">No recent collaboration requests</p>
        </div>
      ) : (
        recentCollaborations.map((collaboration, index) => (
          <div key={index} className="border border-[#3FC4E3] rounded-xl p-4 mt-4">
            <div className="flex flex-col lg:flex-row items-center gap-x-8">
              <img src="/assets/images/icons/glasss.svg" alt="" className="size-[70px]" />
              <div className="w-full mt-3 lg:mt-0">
                <p className="font-popinsSemiBold text-base lg:text-lg text-center lg:text-left">{getRequestTitle(collaboration)}</p>
                <p className="font-popinsRegular text-sm">{collaboration.contactName} ({collaboration.organizationName})</p>
                <p className="font-popinsRegular text-xs text-gray-500 mt-1">Event: {collaboration.eventTitle}</p>
                <div className="flex mt-2 w-full justify-between">
                  <div className="font-popinsMedium text-sm flex items-center gap-x-2">
                    <CalendarMinus2 size={14} />
                    <p>{formatDate(collaboration.createdAt)}</p>
                  </div>
                  <div className="font-popinsMedium text-sm flex items-center gap-x-2">
                    <img src="/assets/images/icons/ino.svg" alt="" className="size-3" />
                    <p>{getStatusText(collaboration)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="flex flex-col lg:flex-row lg:justify-between my-6 w-full">
      <div className="w-full lg:w-[35%]">{activitiesPanel}</div>
      <div className="w-full lg:w-[63%] mt-5 lg:mt-0">{collaborationsPanel}</div>
    </div>
  );
};

export default RecentActivity;
